import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { Form, Button } from 'react-bootstrap';
import { Project, Task, TimeEntry } from '../types/models';
import { TimeSheet } from './TimeSheet';
import './GanttChart.css';

const STATUSES: Task['status'][] = ['pending', 'in-progress', 'completed'];

const STATUS_LABELS: Record<Task['status'], string> = {
  'pending': 'Pending',
  'in-progress': 'In Progress',
  'completed': 'Completed',
};

function totalTime(entries: TimeEntry[]): number {
  return (entries ?? [])
    .filter(e => e.endTime !== null)
    .reduce((sum, e) => sum + (new Date(e.endTime!).getTime() - new Date(e.startTime).getTime()), 0);
}

function formatDuration(ms: number): string {
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return `${h}h ${m.toString().padStart(2, '0')}m`;
}

interface GanttChartProps {
  projects: Project[];
  onTaskMove: (taskId: string, newGoalId: string, newStatus: Task['status'], newOrder: number) => void;
  onAddTimeEntry: (taskId: string, entry: Omit<TimeEntry, 'id'>) => void;
  onDeleteTimeEntry: (taskId: string, entryId: string) => void;
}

export const GanttChart: React.FC<GanttChartProps> = ({
  projects,
  onTaskMove,
  onAddTimeEntry,
  onDeleteTimeEntry,
}) => {
  const [selectedProjectId, setSelectedProjectId] = useState<string>(projects[0]?.id ?? '');
  const [timeSheetTask, setTimeSheetTask] = useState<Task | null>(null);

  useEffect(() => {
    if (!selectedProjectId && projects.length > 0) {
      setSelectedProjectId(projects[0].id);
    }
  }, [projects, selectedProjectId]);

  // Keep timeSheetTask in sync when task data updates (e.g. new entry added)
  useEffect(() => {
    if (!timeSheetTask) return;
    const updated = projects
      .flatMap(p => p.goals.flatMap(g => g.tasks))
      .find(t => t.id === timeSheetTask.id);
    if (updated) setTimeSheetTask(updated);
  }, [projects]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedProject = projects.find(p => p.id === selectedProjectId) ?? null;

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const [goalId, status] = result.destination.droppableId.split(':');
    onTaskMove(result.draggableId, goalId, status as Task['status'], result.destination.index);
  };

  return (
    <div className="gantt-chart">
      <div className="gantt-project-select mb-4">
        <Form.Select
          value={selectedProjectId}
          onChange={e => setSelectedProjectId(e.target.value)}
          disabled={projects.length === 0}
        >
          {projects.length === 0
            ? <option value="">No projects — create one in the Projects tab</option>
            : projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)
          }
        </Form.Select>
      </div>

      {selectedProject && selectedProject.goals.length === 0 && (
        <p className="text-muted ms-1">No goals yet — add one from the Projects tab.</p>
      )}

      {selectedProject && (
        <DragDropContext onDragEnd={handleDragEnd}>
          {selectedProject.goals.map(goal => (
            <div key={goal.id} className="goal-section mb-4">
              <div className="goal-header mb-3">
                <h5 className="mb-0">{goal.title}</h5>
                <span className={`status-badge status-${goal.status}`}>
                  {STATUS_LABELS[goal.status as Task['status']] ?? goal.status}
                </span>
              </div>

              <div className="kanban-board">
                {STATUSES.map(status => {
                  const columnTasks = goal.tasks.filter(t => t.status === status);
                  return (
                    <div key={status} className="kanban-column">
                      <div className={`kanban-column-header kanban-header-${status}`}>
                        <span>{STATUS_LABELS[status]}</span>
                        <span className="kanban-count">{columnTasks.length}</span>
                      </div>
                      <Droppable droppableId={`${goal.id}:${status}`}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className={`kanban-column-body ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}
                          >
                            {columnTasks.map((task, index) => {
                              const total = totalTime(task.timeEntries ?? []);
                              return (
                                <Draggable key={task.id} draggableId={task.id} index={index}>
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      className={`kanban-card ${snapshot.isDragging ? 'dragging' : ''}`}
                                    >
                                      <div className="kanban-card-title">{task.title}</div>
                                      {task.description && (
                                        <div className="kanban-card-desc">{task.description}</div>
                                      )}
                                      {task.assignee && (
                                        <div className="kanban-card-assignee">{task.assignee}</div>
                                      )}
                                      <div className="kanban-card-footer">
                                        <span className="kanban-card-time">
                                          {total > 0 ? `⏱ ${formatDuration(total)}` : '⏱ No time logged'}
                                        </span>
                                        <Button
                                          variant="outline-secondary"
                                          size="sm"
                                          className="kanban-card-time-btn"
                                          onClick={e => {
                                            e.stopPropagation();
                                            setTimeSheetTask(task);
                                          }}
                                        >
                                          Time Sheet
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                </Draggable>
                              );
                            })}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </DragDropContext>
      )}

      {timeSheetTask && (
        <TimeSheet
          task={timeSheetTask}
          show={true}
          onHide={() => setTimeSheetTask(null)}
          onAddEntry={onAddTimeEntry}
          onDeleteEntry={onDeleteTimeEntry}
        />
      )}
    </div>
  );
};
